import * as React from 'react';
import { get, isEqual, isInteger, isNil, isNumber } from 'lodash';

import { Form, FormActions, FormField } from 'components/form';
import Input from 'components/inputs/input/Input';

import { isPositiveNumber } from 'utils/validators';
import { t } from 'utils/language';
import { sanitizeGenericHtml } from 'utils/sanitize';

import type { AdminAnalytic, DeliverableAnalytic } from 'types';

import type { ChangeEvent } from 'react';

type Props = {
  analytic: DeliverableAnalytic | undefined | null,
  onSave: (a: AdminAnalytic) => Promise<void>
};

type State = {
  metric: AdminAnalytic,
  errorOnSave: boolean,
  isChanged: boolean,
  inputError: boolean
};

class MetricsInstagramPostForm extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);

    this.state = {
      metric: {
        reach: get(props, 'analytic.reach', null),
        impressions: get(props, 'analytic.impressions', null),
        saves: get(props, 'analytic.saves', null),
        shares: get(props, 'analytic.shares', null),
        profileVisits: get(props, 'analytic.profileVisits', null),
        likes: get(props, 'analytic.likes', null),
        comments: get(props, 'analytic.comments', null),
        metricsType: 'instagram_post_metrics'
      },
      errorOnSave: false,
      isChanged: false,
      inputError: false
    };
  }

  componentDidUpdate(prevProps: Props) {
    if (!isEqual(this.props.analytic, prevProps.analytic)) {
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState({
        metric: {
          reach: get(this.props, 'analytic.reach', null),
          impressions: get(this.props, 'analytic.impressions', null),
          saves: get(this.props, 'analytic.saves', null),
          shares: get(this.props, 'analytic.shares', null),
          profileVisits: get(this.props, 'analytic.profileVisits', null),
          likes: get(this.props, 'analytic.likes', null),
          comments: get(this.props, 'analytic.comments', null),
          metricsType: 'instagram_post_metrics'
        },
        errorOnSave: false,
        isChanged: false
      });
    }
  }

  handleInputChange = (event: ChangeEvent<HTMLInputElement>): void => {
    const input = event.target;

    this.setState(state => ({
      metric: {
        ...state.metric,
        [input.name]: input.value && isNumber(input.value) ? Number(input.value) : input.value
      },
      isChanged: true,
      errorOnSave: false,
      inputError: false
    }));
  };

  handleSubmit = (metricType?: string) => {
    const { onSave } = this.props;
    const { metric, isChanged } = this.state;
    const metricValidation = metricType ? isInteger(Number(metric[metricType])) : true;

    if (isChanged && metricValidation) {
      onSave(metric)
        .then(() => this.setState({ isChanged: false }))
        .catch(() => this.setState({ errorOnSave: true }));
    } else if (isChanged && metricType) {
      this.setState(state => ({
        inputError: true,
        metric: {
          ...state.metric,
          [metricType]: null
        }
      }));
    }
  };

  render(): React.ReactNode {
    const { errorOnSave, inputError, metric } = this.state;
    const isDisabled = !this.props.analytic;

    return (
      <section className="deliverables-metrics-instagram-post-form">
        <Form
          onSubmit={() => this.handleSubmit()}
        >
          <FormField
            direction="column"
            label={t('admin_deliverables_metrics_page.edit_form.label_reach')}
            labelType="highlighted"
            validators={[
              isPositiveNumber(metric.reach)
            ]}
          >
            <Input
              type="text"
              name="reach"
              width="short"
              value={isNil(metric.reach) ? '' : String(metric.reach)}
              onChange={this.handleInputChange}
              onBlur={() => this.handleSubmit('reach')}
            />
          </FormField>
          <FormField
            direction="column"
            label={t('admin_deliverables_metrics_page.edit_form.label_impressions')}
            labelType="highlighted"
            validators={[
              isPositiveNumber(metric.impressions)
            ]}
          >
            <Input
              type="text"
              name="impressions"
              width="short"
              value={isNil(metric.impressions) ? '' : String(metric.impressions)}
              onChange={this.handleInputChange}
              onBlur={() => this.handleSubmit('impressions')}
            />
          </FormField>
          <FormField
            direction="column"
            label={t('admin_deliverables_metrics_page.edit_form.label_likes')}
            labelType="highlighted"
            validators={[
              isPositiveNumber(metric.likes)
            ]}
          >
            <Input
              type="text"
              name="likes"
              width="short"
              value={isNil(metric.likes) ? '' : String(metric.likes)}
              onChange={this.handleInputChange}
              onBlur={() => this.handleSubmit('likes')}
            />
          </FormField>
          <FormField
            direction="column"
            label={t('admin_deliverables_metrics_page.edit_form.label_comments')}
            labelType="highlighted"
            validators={[
              isPositiveNumber(metric.comments)
            ]}
          >
            <Input
              type="text"
              name="comments"
              width="short"
              value={isNil(metric.comments) ? '' : String(metric.comments)}
              onChange={this.handleInputChange}
              onBlur={() => this.handleSubmit('comments')}
            />
          </FormField>
          <FormField
            direction="column"
            label={t('admin_deliverables_metrics_page.edit_form.label_saves')}
            labelType="highlighted"
            validators={[
              isPositiveNumber(metric.saves)
            ]}
          >
            <Input
              type="text"
              name="saves"
              width="short"
              value={isNil(metric.saves) ? '' : String(metric.saves)}
              onChange={this.handleInputChange}
              onBlur={() => this.handleSubmit('saves')}
            />
          </FormField>
          <FormField
            direction="column"
            label={t('admin_deliverables_metrics_page.edit_form.label_profile_visits')}
            labelType="highlighted"
            validators={[
              isPositiveNumber(metric.profileVisits)
            ]}
          >
            <Input
              type="text"
              name="profileVisits"
              width="short"
              value={isNil(metric.profileVisits) ? '' : String(metric.profileVisits)}
              onChange={this.handleInputChange}
              onBlur={() => this.handleSubmit('profileVisits')}
            />
          </FormField>
          <FormActions>
            {
              isDisabled && (
                <span
                  className="deliverables-metrics-instagram-post-form__error"
                  dangerouslySetInnerHTML={{ __html: sanitizeGenericHtml(t('admin_deliverables_metrics_page.edit_form.message_error')) }}
                />
              )
            }
            {
              (errorOnSave || inputError) && (
                <span
                  className="deliverables-metrics-instagram-post-form__error"
                  dangerouslySetInnerHTML={{ __html: sanitizeGenericHtml(t(`admin_deliverables_metrics_page.edit_form.${errorOnSave ? 'save_error' : 'input_error'}`)) }}
                />
              )
            }
          </FormActions>
        </Form>
      </section>
    );
  }
}

export default MetricsInstagramPostForm;
